import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { steps } from "../../../../data/DataFile";
import HeaderNew from "../../../../common/components/custom/header-new/HeaderNew";
import HubHeader from "../../../../common/components/custom/hub-header/HubHeader";
import SidebarConfigureOps from "../sidebar-configure-ops/SidebarConfigureOps";
import { useCreateAssignmentMutation } from "../../data/api/commands/assignmentCommands";
import { CreateAssignmentRequest } from "../../data/models/configuration-hub.model";
import { FlowConfigService } from "../../data/services/flow-config.service";
import { ToastMessageUtil } from "../../../../common/components/base/toast-message/ToastMessage.util";
import { ToastVariant } from "../../../../common/enums/controls/toast.enum";
import {
  useAppDispatch,
  useAppSelector
} from "../../../../common/redux/core/root.reducer";
import { setSelectedDocumentType } from "../../../_shared/redux/document-type";
import "./ConfigureOps.scss";

const ConfigureOps: React.FC = () => {
  const [flowName, setFlowName] = useState<string>("");
  const [flowDescription, setFlowDescription] = useState<string>("");
  const [flowNameError, setFlowNameError] = useState<string>("");
  const [selectedOperations, setSelectedOperations] = useState<string[]>([]);
  const [isDocOperationsOpen, setIsDocOperationsOpen] =
    useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Read URL parameters for configId and configName from Create Flow navigation
  const urlParams = new URLSearchParams(location.search);
  const configIdFromUrl = urlParams.get("configId");
  const configNameFromUrl = urlParams.get("configName");
  const documentTypeFromUrl = urlParams.get("documentType");

  const { selectedDocumentType, documentTypeOptions } = useAppSelector(
    (state) => state.documentType
  );
  const [createAssignment, { isLoading }] = useCreateAssignmentMutation();

  // Check if in edit mode
  const editMode = location.state?.editMode;
  const configData = location.state?.configData;
  const navigationDocumentType = location.state?.documentType;

  // Pre-fill form in edit mode
  useEffect(() => {
    if (editMode && configData) {
      setFlowName(configData.flow_name || "");
      setFlowDescription(configData.flow_description || "");

      // Set document type from navigation state if available
      if (navigationDocumentType) {
        dispatch(setSelectedDocumentType(navigationDocumentType));
      }
    }
  }, [editMode, configData, navigationDocumentType, dispatch]);

  // Sync document type from URL to Redux state when coming from Create Flow
  useEffect(() => {
    if (documentTypeFromUrl && !editMode) {
      const decodedDocumentType = decodeURIComponent(documentTypeFromUrl);
      if (decodedDocumentType !== selectedDocumentType) {
        dispatch(setSelectedDocumentType(decodedDocumentType));
      }
    }
  }, [documentTypeFromUrl, selectedDocumentType, dispatch, editMode]);

  // Handle URL parameters from extraction configuration or other sources
  useEffect(() => {
    const flowNameFromUrl = urlParams.get("flowName");
    const flowDescriptionFromUrl = urlParams.get("flowDescription");

    // Pre-populate form fields if they come from URL (extraction config navigation)
    if (flowNameFromUrl && !editMode && !flowName) {
      setFlowName(decodeURIComponent(flowNameFromUrl));
    }
    if (flowDescriptionFromUrl && !editMode && !flowDescription) {
      setFlowDescription(decodeURIComponent(flowDescriptionFromUrl));
    }
  }, [urlParams, editMode, flowName, flowDescription]);

  //   const isDocTypeSelected = flowName !== "";
  const isFormValid = flowName.trim() !== "" && flowDescription.trim() !== "";
  const currentStep = 1;

  const toggleDocOperations = useCallback(() => {
    setIsDocOperationsOpen(!isDocOperationsOpen);
  }, [isDocOperationsOpen]);

  const handleDocumentTypeChange = useCallback(
    (value: string) => {
      dispatch(setSelectedDocumentType(value));
    },
    [dispatch]
  );

  const handleNext = useCallback(async () => {
    if (!isFormValid) {
      // Show toast message to user about validation
      ToastMessageUtil.show(
        "Please fill in all required fields (Flow name and Flow description)",
        ToastVariant.ERROR
      );
      return;
    }

    // Clear any previous flow name error
    setFlowNameError("");

    // Validate flow name for duplicates (only for new flow configurations, not edits)
    if (!editMode) {
      try {
        const allFlowsResult =
          await FlowConfigService.getAllFlowConfigurations();
        if (!allFlowsResult.hasErrors && allFlowsResult.value) {
          const existingFlowNames = allFlowsResult.value.map((config) =>
            config.flow_name.toLowerCase().trim()
          );
          const currentFlowName = flowName.toLowerCase().trim();

          if (existingFlowNames.includes(currentFlowName)) {
            setFlowNameError(
              "Configuration name already exists. Please choose a different name."
            );
            return;
          }
        }
      } catch (error) {
        console.error("Error checking for duplicate flow names:", error);
        setFlowNameError("Unable to validate flow name. Please try again.");
        return;
      }
    }

    // In edit mode, skip assignment creation and navigate with config data
    if (editMode && configData) {
      const params = new URLSearchParams({
        editMode: "true",
        configId: configData.id?.toString() || "",
        flowName: encodeURIComponent(flowName.trim()),
        flowDescription: encodeURIComponent(flowDescription.trim()),
        documentType: encodeURIComponent(selectedDocumentType || ""),
        ingestion: configData.ingestion?.toString() || "false",
        extraction: configData.extraction?.toString() || "false",
        tagging: configData.tagging?.toString() || "false",
        similarity: configData.similarity?.toString() || "false",
        export: configData.export?.toString() || "false"
      });

      navigate(`/configure-ops/document-operation?${params.toString()}`);
      return;
    }

    try {
      // Create assignment via API for new configs
      const assignmentData: CreateAssignmentRequest = {
        title: flowName,
        company: "EY", // Could be dynamic
        department: "Technology" // Could be dynamic
      };

      // const result = await createAssignment(assignmentData).unwrap();
      const result = { id: "" };

      // Navigate to document operation with assignment ID and flow data
      const params = new URLSearchParams({
        assignmentId: result?.id?.toString() || "new",
        flowName: encodeURIComponent(flowName.trim()),
        flowDescription: encodeURIComponent(flowDescription.trim()),
        documentType: encodeURIComponent(selectedDocumentType || "")
      });

      // Preserve configId and configName from URL if they exist (from Create Flow)
      if (configIdFromUrl) {
        params.set("configId", configIdFromUrl);
      }
      if (configNameFromUrl) {
        params.set("configName", configNameFromUrl);
      }

      navigate(`/configure-ops/document-operation?${params.toString()}`);
    } catch (error) {
      console.error("Error creating assignment:", error);
      // Still navigate on error for now with flow data
      const params = new URLSearchParams({
        assignmentId: "new",
        flowName: encodeURIComponent(flowName.trim()),
        flowDescription: encodeURIComponent(flowDescription.trim()),
        documentType: encodeURIComponent(selectedDocumentType || "")
      });

      // Preserve configId and configName from URL if they exist (from Create Flow)
      if (configIdFromUrl) {
        params.set("configId", configIdFromUrl);
      }
      if (configNameFromUrl) {
        params.set("configName", configNameFromUrl);
      }

      navigate(`/configure-ops/document-operation?${params.toString()}`);
    }
  }, [
    isFormValid,
    flowName,
    flowDescription,
    selectedDocumentType,
    editMode,
    configData,
    createAssignment,
    navigate,
    configIdFromUrl,
    configNameFromUrl,
    isLoading
  ]);

  return (
    <>
      <HeaderNew showSearch={false} />
      <div className="configure-ops-container">
        {/* Header */}
        <HubHeader
          className="header"
          showBackButton={true}
          backRoute="/assignment"
        />

        {/* Main Config Box */}
        <div className="config-box">
          <div className="main-area">
            {/* Sidebar Component */}
            <SidebarConfigureOps
              //   isDocTypeSelected={isDocTypeSelected}
              isDocOperationsOpen={isDocOperationsOpen}
              selectedOperations={selectedOperations}
              onToggleDocOperations={toggleDocOperations}
              currentStep={currentStep}
              operations={steps}
            />

            {/* Form Area */}
            <div className="form-area">
              <div className="form-field">
                <label>
                  Document type <span style={{ color: "red" }}>*</span>
                </label>
                <div className="dropdown-container">
                  <select
                    className="document-type-dropdown"
                    value={selectedDocumentType}
                    onChange={(e) => handleDocumentTypeChange(e.target.value)}
                  >
                    {documentTypeOptions.map((option: any) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <div className="dropdown-arrow">
                    <svg
                      width="12"
                      height="7"
                      viewBox="0 0 12 7"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1 1L6 6L11 1"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="form-field">
                <label>
                  Flow name <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter flow name"
                  value={flowName}
                  onChange={(e) => {
                    setFlowName(e.target.value);
                    // Clear error when user starts typing
                    if (flowNameError) {
                      setFlowNameError("");
                    }
                  }}
                />
                {flowNameError && (
                  <div
                    style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
                  >
                    {flowNameError}
                  </div>
                )}
              </div>{" "}
              <div className="form-field">
                <label>
                  Flow description <span style={{ color: "red" }}>*</span>
                </label>
                <textarea
                  rows={4}
                  placeholder="Enter the description about the flow which you are creating. Mention the steps used, if possible."
                  value={flowDescription}
                  onChange={(e) => setFlowDescription(e.target.value)}
                />
              </div>
              <div className="help-text">
                This is a hint text to help users.
                <span className="char-count">
                  {flowDescription.length}/2000
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Action Buttons */}
        <div className="main-action-buttons">
          <button className="cancel-btn" onClick={() => navigate(-1)}>
            Cancel
          </button>
          <button
            className={`next-btn ${
              !isFormValid || isLoading ? "disabled" : ""
            }`}
            onClick={(e) => {
              e.preventDefault();
              if (!isFormValid || isLoading) {
                return;
              }
              handleNext();
            }}
            disabled={!isFormValid || isLoading}
            type="button"
          >
            {isLoading || !isFormValid ? "Creating..." : "Next"}
          </button>
        </div>
      </div>
    </>
  );
};

export default ConfigureOps;
